/**
 * @fileoverview Request building and role labels for the Add User invitation form.
 * @description The pure pieces behind AddUserModal: the display labels for the sales roles and the
 * reporting-manager positions, and the conversion of the validated form values into the
 * invitation request body. Kept out of the component so it stays within the repo's file-size
 * limit and so the mapping can be read and tested on its own.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import {
  INVITE_COUNTRY_CODE,
  MANAGER_REQUIRED_ROLE_CODES,
} from '@/features/user-invitations/constants/invitation-roles.constants'

import type { AddUserFormValues } from '@/features/user-invitations/schemas/add-user-form.schema'
import type {
  AssignableRole,
  SendInvitationRequest,
} from '@/features/user-invitations/types/user-invitations.types'

/** The finalized form's role labels, by backend role code. */
export const ROLE_LABELS: Record<string, string> = {
  SALES_HEAD: 'Sales Head',
  SALES_MANAGER: 'Sales Manager',
  SALES_EXECUTIVE: 'Sales Executive',
}

/** Reporting-manager position labels, by the backend's position code. */
export const POSITION_LABELS: Record<string, string> = {
  PLATFORM_ADMIN: 'Super Admin',
  ...ROLE_LABELS,
}

/**
 * @description Builds the invitation request from the validated form.
 * @param {AddUserFormValues} values - The form values.
 * @param {AssignableRole} role - The chosen role.
 * @returns {SendInvitationRequest} The request body.
 */
export function toSendInvitationRequest(
  values: AddUserFormValues,
  role: AssignableRole,
): SendInvitationRequest {
  return {
    email: values.email,
    firstName: values.firstName,
    lastName: values.lastName,
    countryCode: INVITE_COUNTRY_CODE,
    mobileNumber: values.phone,
    departmentId: values.departmentId,
    // Left out for All India, which the backend reads as no state.
    ...(values.stateId && { stateId: values.stateId }),
    ...(MANAGER_REQUIRED_ROLE_CODES.has(role.roleCode) &&
      values.reportsTo && { reportingManagerUserId: values.reportsTo }),
    assignments: [{ roleId: role.roleId, scopes: [{ scopeType: 'PLATFORM' }] }],
  }
}
